import { create } from "zustand";
import axios from "axios";
import { api } from "./api";

export const useAuthStore = create((set, get) => {
    let refreshTimeout;

    const scheduleTokenRefresh = (expiryDate) => {
        if (refreshTimeout) clearTimeout(refreshTimeout);

        const expiresInMs = new Date(expiryDate).getTime() - Date.now();
        const refreshInMs = expiresInMs - 60 * 1000; // обновляем за 1 минуту до конца

        if (refreshInMs > 0) {
            refreshTimeout = setTimeout(() => {
                get().refreshAccessToken();
            }, refreshInMs);
        }
    };

    return {
        token: localStorage.getItem("access_token") || "",
        refreshToken: localStorage.getItem("refresh_token") || "",
        user: JSON.parse(localStorage.getItem("user")) || null,
        expiryDate: localStorage.getItem("expiry_date") || null,
        loading: false,
        error: null,

        register: async (userData) => {
            set({ loading: true, error: null });
            try {
                const { data } = await axios.post(`${api}/auth/register`, userData);

                localStorage.setItem("access_token", data.tokens.access_token.token);
                localStorage.setItem("refresh_token", data.tokens.refresh_token);
                localStorage.setItem("expiry_date", data.tokens.access_token.expiry_date);
                localStorage.setItem("user", JSON.stringify(data.user));

                set({
                    user: data.user,
                    token: data.tokens.access_token.token,
                    refreshToken: data.tokens.refresh_token,
                    expiryDate: data.tokens.access_token.expiry_date,
                    loading: false,
                });

                scheduleTokenRefresh(data.tokens.access_token.expiry_date);

                return true;
            } catch (e) {
                set({ error: e.response?.data || "Ошибка регистрации", loading: false });
                console.error(e);
                return false;
            }
        },

        login: async (userData) => {
            set({ loading: true, error: null });
            try {
                const { data } = await axios.post(`${api}/auth/login`, userData);

                localStorage.setItem("access_token", data.tokens.access_token.token);
                localStorage.setItem("refresh_token", data.tokens.refresh_token);
                localStorage.setItem("expiry_date", data.tokens.access_token.expiry_date);
                localStorage.setItem("user", JSON.stringify(data.user));

                set({
                    user: data.user,
                    token: data.tokens.access_token.token,
                    refreshToken: data.tokens.refresh_token,
                    expiryDate: data.tokens.access_token.expiry_date,
                    loading: false,
                });

                scheduleTokenRefresh(data.tokens.access_token.expiry_date);
                return true;
            } catch (e) {
                set({ error: e.response?.data || "Ошибка входа", loading: false });
                console.error(e);
                return false;
            }
        },
        reset: async (email) => {
            set({ loading: true, error: null });
            try {
                await axios.post(`${api}/auth/password/reset`, { email });

                set({ loading: false });
                return true;
            } catch (e) {
                set({ error: e.response?.data || "Ошибка входа", loading: false });
                console.error(e);
            }
        },


        refreshAccessToken: async () => {
            const refreshToken = get().refreshToken || localStorage.getItem("refresh_token");
            if (!refreshToken) return;

            try {
                const { data } = await axios.get(
                    `${api}/auth/refresh-access-token?refresh_token=${refreshToken}`
                );
                const accessToken = data.access_token || data.accessToken;

                if (!accessToken?.token || !accessToken?.expiry_date) {
                    throw new Error("Некорректный ответ обновления токена");
                }

                localStorage.setItem("access_token", accessToken.token);
                localStorage.setItem("expiry_date", accessToken.expiry_date);

                set({
                    token: accessToken.token,
                    expiryDate: accessToken.expiry_date,
                });

                scheduleTokenRefresh(accessToken.expiry_date);
                return accessToken.token;
            } catch (e) {
                console.error("Ошибка обновления токена:", e);
                set({ token: "", user: null });
                localStorage.clear();
                return null;
            }
        },

        initAuth: () => {
            const expiryDate = localStorage.getItem("expiry_date");
            if (!expiryDate) return;

            const expiresInMs = new Date(expiryDate).getTime() - Date.now();

            if (expiresInMs <= 0) {
                // Токен уже истёк — обновляем немедленно
                get().refreshAccessToken();
            } else {
                // Токен ещё действителен — планируем обновление
                scheduleTokenRefresh(expiryDate);
            }
        },

        logout: () => {
            if (refreshTimeout) clearTimeout(refreshTimeout);
            localStorage.clear();
            set({ token: "", refreshToken: "", user: null, expiryDate: null });
        },
    };
});

export const useRegister = () => {
    const register = useAuthStore((state) => state.register);
    const login = useAuthStore((state) => state.login);
    const logout = useAuthStore((state) => state.logout);
    const refreshAccessToken = useAuthStore((state) => state.refreshAccessToken);
    const initAuth = useAuthStore((state) => state.initAuth);
    const reset = useAuthStore((state) => state.reset);
    const loading = useAuthStore((state) => state.loading);
    const error = useAuthStore((state) => state.error);
    return { register, refreshAccessToken, initAuth, login, reset, loading, error, logout };
};
